// src\App.tsx
import { ThemeProvider, CssBaseline, } from "@mui/material";
import theme /*{ getTheme } */ from "./theme/theme";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from 'react-toastify';
import { AppLoader } from "./components";
import RoutesComponent from "./routes";
import { useEffect } from "react";
import { requestNotificationPermission, getFCMMessaging } from "./firebase";
import { onMessage } from "firebase/messaging";
import { incrementNotification, setNotificationCount } from "./stores/notificationSlice";
import { GlobalContextProvider } from "./context/GlobalContext";
import { io } from "socket.io-client";
import { BASE_URL } from "./Config";
import { RootState } from "./stores";
import { logout } from "./stores/authSlice";
import { AuthService } from "./services";
import Swal from "sweetalert2";
// import { useState } from "react";

export default function App() {
  // const [darkMode, setDarkMode] = useState(false);
  const dispatch = useDispatch();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  useEffect(() => {
    requestNotificationPermission().then(token => {
      if (token) {
        console.log("FCM Token generated:");
        localStorage.setItem("fcmToken", token);
      }
    });

    let unsubscribeOnMessage: (() => void) | undefined;
    // Foreground notifications
    getFCMMessaging().then(msg => {
      if (!msg) return;
      unsubscribeOnMessage = onMessage(msg, payload => {
        console.log("On-screen msg:", payload);
        const title = payload.data?.title || payload.notification?.title;
        if (title) {
          toast.info(title);
          dispatch(incrementNotification());
        }
      });
    });


    // BroadcastChannel set up
    const bc = new BroadcastChannel('notifications_channel');
    bc.onmessage = (event) => {
      console.log(event, 'event');
      // only increment for background events
      if (/*document.visibilityState === 'hidden' &&*/ event.data?.type === 'TICKET_ASSIGNED') {
        dispatch(incrementNotification());
      }
    };

    // Socket.IO notifications count
    if (!accessToken) {
      console.warn("[socket.io] skipped connect: no access token");
      return () => {
        bc.close();
        if (unsubscribeOnMessage) unsubscribeOnMessage();  // clean up onMessage
      };
    }

    const socket = io(BASE_URL, {
      transports: ["websocket"],
      auth: { token: accessToken },
    });
    socket.on("connect", () => {
      console.log("[socket.io] connected", socket.id);
    });
    socket.on("connect_error", (err) => {
      console.error("[socket.io] connect_error", err?.message || err);
    });
    socket.on("disconnect", (reason) => {
      console.warn("[socket.io] disconnected", reason);
    });
    socket.on("error", (err) => {
      console.error("[socket.io] error", err);
    });
    socket.onAny((event, ...args) => {
      if (event !== "ping" && event !== "pong") {
        console.log("[socket.io] event", event, args);
      }
    });
    socket.on("notification:count", (payload: { unreadCount?: number }) => {
      console.log("[socket.io] notification:count", payload);
      if (typeof payload?.unreadCount === "number") {
        dispatch(setNotificationCount(payload.unreadCount));
      }
    });
    socket.on("auth:force-logout", async () => {
      AuthService.logout({ token: localStorage.getItem('fcmToken') });
      dispatch(logout());
      await Swal.fire({
        icon: 'info',
        title: 'Session Ended',
        text: 'Your session has ended because you logged in on another device.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#002F73',
      });
      window.location.href = '/login';
    });
    return () => {
      bc.close();
      if (unsubscribeOnMessage) unsubscribeOnMessage();  // clean up onMessage
      socket.off("notification:count");
      socket.off("auth:force-logout");
      socket.disconnect();
    };
  }, [dispatch, accessToken]);

  return <>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalContextProvider>
        <RoutesComponent />
      </GlobalContextProvider>
      <ToastContainer
        style={{ zIndex: 10001 }}
        autoClose={3000}
        hideProgressBar
        pauseOnHover
      />
      <AppLoader />
    </ThemeProvider>
  </>
}
